import { autoBrightness } from '@cloudinary/url-gen/actions/adjust';
import { format, quality } from '@cloudinary/url-gen/actions/delivery';
import { thumbnail } from '@cloudinary/url-gen/actions/resize';
import { byRadius } from '@cloudinary/url-gen/actions/roundCorners';
import { FocusOn } from '@cloudinary/url-gen/qualifiers/focusOn';
import { focusOn } from '@cloudinary/url-gen/qualifiers/gravity';
import { api } from '../api';
import {
	AVATAR_SIZE,
	fallbackNameUrl,
	ICON_SIZE,
	IconName,
	iconUrl,
	LOGO_HEIGHT,
	LOGO_WIDTH,
	logoUrl,
	NAME_FONT_SIZE,
	NameImage,
} from '../utils/signatureAssets';
import '../styles/components/Generated.scss';

type GeneratedProps = {
	name: string;
	occupation: string;
	phone: string;
	email: string;
	publicId: string;
	nameImage?: NameImage | null;
};

const WEB_URL = 'https://www.reuni.cz';
const WEB_LABEL = 'www.reuni.cz';

const DIVIDER_COLOR = '#685BD2';
const TEXT_COLOR = '#767676';
const MUTED_COLOR = '#8F8F8F';
const FONT_STACK = "'Helvetica Neue', Helvetica, Arial, sans-serif";

const SECTION_GAP = 28;
const AVATAR_GAP = 17;
const GROUP_GAP = 28;
const ICON_GAP = 8;
const ROW_GAP = 6;

const nameFallbackText = {
	fontFamily: FONT_STACK,
	fontSize: `${NAME_FONT_SIZE}px`,
	fontWeight: 600,
	color: DIVIDER_COLOR,
};

const bodyText = {
	fontFamily: FONT_STACK,
	fontSize: '11px',
	lineHeight: '12px',
	textAlign: 'left' as const,
};

const resetImage = {
	border: 0,
	display: 'block',
	outline: 'none',
	textDecoration: 'none',
};

const Spacer = ({ width }: { width: number }) => (
	<td width={width} style={{ width: `${width}px`, fontSize: 0, lineHeight: 0 }}>
		&nbsp;
	</td>
);

type ContactRowProps = {
	icon: IconName;
	href: string;
	label: string;
	isFirst: boolean;
};

const ContactRow = ({ icon, href, label, isFirst }: ContactRowProps) => (
	<tr>
		<td style={{ paddingTop: isFirst ? 0 : `${ROW_GAP}px`, verticalAlign: 'middle' }}>
			<img
				src={iconUrl(icon)}
				width={ICON_SIZE}
				height={ICON_SIZE}
				alt=""
				style={{ ...resetImage, width: `${ICON_SIZE}px`, height: `${ICON_SIZE}px` }}
			/>
		</td>
		<Spacer width={ICON_GAP} />
		<td style={{ ...bodyText, paddingTop: isFirst ? 0 : `${ROW_GAP}px`, verticalAlign: 'middle' }}>
			<a href={href} style={{ ...bodyText, color: TEXT_COLOR, textDecoration: 'none' }}>
				{label}
			</a>
		</td>
	</tr>
);

const Generated = ({ name, occupation, phone, email, publicId, nameImage }: GeneratedProps) => {
	const avatar = publicId ? api?.image(publicId) : null;

	const avatarSource = AVATAR_SIZE * 2;
	const avatarRadius = Math.round(avatarSource * 0.3);

	const fallbackUrl = nameImage ? null : fallbackNameUrl(name);

	avatar
		?.resize(thumbnail().width(avatarSource).height(avatarSource).zoom(0.8).gravity(focusOn(FocusOn.face())))
		.adjust(autoBrightness())
		.delivery(quality(90))
		.roundCorners(byRadius(avatarRadius, avatarRadius, avatarRadius, 0))
		.delivery(format('png'));

	const avatarUrl = avatar?.toURL();

	return (
		<table
			id="table-root"
			role="presentation"
			cellPadding={0}
			cellSpacing={0}
			border={0}
			style={{ borderCollapse: 'collapse', wordSpacing: 'normal' }}
		>
			<tbody>
				<tr>
					<td style={{ paddingBottom: `${SECTION_GAP}px` }}>
						<img
							src={logoUrl}
							width={LOGO_WIDTH}
							height={LOGO_HEIGHT}
							alt="reuni"
							style={{ ...resetImage, width: `${LOGO_WIDTH}px`, height: `${LOGO_HEIGHT}px` }}
						/>
					</td>
				</tr>

				<tr>
					<td
						style={{
							borderTop: `1px solid ${DIVIDER_COLOR}`,
							fontSize: 0,
							lineHeight: 0,
							height: '1px',
						}}
					>
						&nbsp;
					</td>
				</tr>

				<tr>
					<td style={{ paddingTop: `${SECTION_GAP}px` }}>
						<table role="presentation" cellPadding={0} cellSpacing={0} border={0}>
							<tbody>
								<tr>
									{avatarUrl && (
										<>
											<td style={{ verticalAlign: 'middle', width: `${AVATAR_SIZE}px` }}>
												<img
													src={avatarUrl}
													width={AVATAR_SIZE}
													height={AVATAR_SIZE}
													alt={name}
													style={{
														...resetImage,
														width: `${AVATAR_SIZE}px`,
														height: `${AVATAR_SIZE}px`,
													}}
												/>
											</td>
											<Spacer width={AVATAR_GAP} />
										</>
									)}

									<td style={{ verticalAlign: 'middle' }}>
										<table role="presentation" cellPadding={0} cellSpacing={0} border={0}>
											<tbody>
												<tr>
													<td style={{ fontSize: 0, lineHeight: 0 }}>
														{nameImage ? (
															<img
																src={nameImage.url}
																width={nameImage.width}
																height={nameImage.height}
																alt={name}
																style={{
																	...resetImage,
																	...nameFallbackText,
																	width: `${nameImage.width}px`,
																	height: `${nameImage.height}px`,
																}}
															/>
														) : (
															fallbackUrl && (
																<img
																	src={fallbackUrl}
																	alt={name}
																	style={{ ...resetImage, ...nameFallbackText }}
																/>
															)
														)}
													</td>
												</tr>
												<tr>
													<td
														style={{
															...bodyText,
															color: MUTED_COLOR,
															letterSpacing: '0.8px',
															paddingTop: '4px',
														}}
													>
														{/* Uppercased here, not via text-transform, which
														    some clients drop. */}
														{occupation.toUpperCase()}
													</td>
												</tr>
											</tbody>
										</table>
									</td>

									<Spacer width={GROUP_GAP} />

									<td style={{ verticalAlign: 'middle' }}>
										<table role="presentation" cellPadding={0} cellSpacing={0} border={0}>
											<tbody>
												<ContactRow
													icon="phone"
													href={`tel:${phone.split(' ').join('')}`}
													label={phone}
													isFirst={true}
												/>
												<ContactRow
													icon="mail"
													href={`mailto:${email.toLowerCase()}`}
													label={email.toLowerCase()}
													isFirst={false}
												/>
												<ContactRow
													icon="link"
													href={WEB_URL}
													label={WEB_LABEL}
													isFirst={false}
												/>
											</tbody>
										</table>
									</td>
								</tr>
							</tbody>
						</table>
					</td>
				</tr>
			</tbody>
		</table>
	);
};

export default Generated;
